import { FC, useCallback, useState } from 'react'
import { Button, FetchState, InfoAlert, ListLoading, useModal } from 'app/components'
import { LotacaoView } from '../../core/_lotacaoModels'
import { MSG_SEM_PERMISSAO_LOTACAO, usePermissaoLotacaoEscrita } from '../../core/_lotacaoPermissao'
import { deleteLotacao, getLotacoesByFuncionario, reactivateLotacao } from '../../core/_requests'
import { ButtonType, IconPaths, showAlert, showConfirmAlert, showError } from '_metronic/helpers'
import { LotacaoForm } from './LotacaoForm'
import { LotacoesTable } from './LotacoesTable'

type Props = {
  funcionarioId: number
  empresaId?: number
}

const avisarSemPermissao = () =>
  showAlert({ title: 'Sem permissão', text: MSG_SEM_PERMISSAO_LOTACAO, icon: 'warning' })

export const LotacoesModal: FC<Props> = ({ funcionarioId, empresaId }) => {
  const { fetchStates, setData } = useModal()

  const { data: lotacoes, isLoading } = (fetchStates['lotacoes'] || {}) as FetchState<LotacaoView[]>

  const [showForm, setShowForm] = useState(false)
  const [editingLotacao, setEditingLotacao] = useState<LotacaoView | undefined>(undefined)

  // Espelha os grupos autorizados para Lotacao:create/update/delete em permission.json (backend)
  const podeEditar = usePermissaoLotacaoEscrita()

  const total = lotacoes?.length || 0
  const ativas = lotacoes?.filter((l) => l.ativo).length || 0

  const refreshLotacoes = useCallback(async () => {
    try {
      const data = await getLotacoesByFuncionario(funcionarioId)
      setData('lotacoes', Array.isArray(data) ? data : [])
    } catch (err) {
      showError(err)
    }
  }, [funcionarioId, setData])

  const handleOpenNewForm = () => {
    if (!podeEditar) {
      avisarSemPermissao()
      return
    }
    setEditingLotacao(undefined)
    setShowForm(true)
  }

  const handleOpenEditForm = useCallback(
    (lotacao: LotacaoView) => {
      if (!podeEditar) {
        avisarSemPermissao()
        return
      }
      setEditingLotacao(lotacao)
      setShowForm(true)
    },
    [podeEditar]
  )

  const handleSaved = () => {
    setShowForm(false)
    setEditingLotacao(undefined)
    refreshLotacoes()
  }

  const handleCancelForm = () => {
    setShowForm(false)
    setEditingLotacao(undefined)
  }

  const handleDelete = useCallback(
    async (lotacao: LotacaoView) => {
      if (!podeEditar) {
        avisarSemPermissao()
        return
      }
      const confirmed = await showConfirmAlert({
        title: 'Inativar lotação?',
        text: `A lotação em ${
          lotacao.unidade?.nome || 'esta unidade'
        } deixará de ser enviada ao e-SUS. Você pode reativá-la quando quiser.`,
        confirmButtonText: 'Inativar',
        cancelButtonText: 'Cancelar',
        danger: true,
      })
      if (!confirmed) return
      try {
        await deleteLotacao(lotacao.id)
        showAlert({ title: 'Sucesso!', text: 'Lotação inativada com sucesso!', icon: 'success' })
        refreshLotacoes()
      } catch (err) {
        showError(err)
      }
    },
    [podeEditar, refreshLotacoes]
  )

  const handleReactivate = useCallback(
    async (lotacao: LotacaoView) => {
      if (!podeEditar) {
        avisarSemPermissao()
        return
      }
      try {
        await reactivateLotacao(lotacao.id)
        showAlert({ title: 'Sucesso!', text: 'Lotação reativada com sucesso!', icon: 'success' })
        refreshLotacoes()
      } catch (err) {
        showError(err)
      }
    },
    [podeEditar, refreshLotacoes]
  )

  return (
    <>
      {!podeEditar && (
        <InfoAlert variant='info' title='Somente leitura'>
          {MSG_SEM_PERMISSAO_LOTACAO}
        </InfoAlert>
      )}

      {isLoading ? (
        <ListLoading />
      ) : showForm ? (
        podeEditar && (
          <LotacaoForm
            funcionarioId={funcionarioId}
            empresaId={empresaId}
            lotacao={editingLotacao}
            onSaved={handleSaved}
            onCancel={handleCancelForm}
          />
        )
      ) : (
        <>
          <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4'>
            <span className='fs-7'>
              <span className='fw-semibold text-gray-900'>
                {total} {total === 1 ? 'lotação' : 'lotações'}
              </span>
              <span className='text-muted'>
                {' · '}
                {ativas} {ativas === 1 ? 'ativa' : 'ativas'}
              </span>
            </span>
            {podeEditar && (
              <Button
                type={ButtonType.Button}
                onClick={handleOpenNewForm}
                className='btn-primary'
                size='btn-sm'
                icon={IconPaths.Add}
              >
                Nova lotação
              </Button>
            )}
          </div>

          <LotacoesTable
            lotacoes={lotacoes || []}
            podeEditar={podeEditar}
            onEdit={handleOpenEditForm}
            onDelete={handleDelete}
            onReactivate={handleReactivate}
          />
        </>
      )}
    </>
  )
}
